import clsx from 'clsx';
import type * as React from 'react';

import type { AlertAction as AlertActionType, AlertKind } from '../Alert';
import {
    AlertAction,
    AlertCloseButton,
    AlertDetails,
    AlertTitle,
    alertKindPropsMap,
} from '../Alert';
import { renderIcon } from '../util';

export type AlertBannerProps = {
    kind?: AlertKind;
    title?: string;
    details?: React.ReactNode;
    action?: AlertActionType;
    onClose?: () => void;
    className?: string;
    style?: React.CSSProperties;
    testID?: string;
};

export function AlertBanner(props: AlertBannerProps): React.ReactElement {
    const { kind = 'default', title, details, action, onClose, className, style, testID } = props;

    const kindProps = alertKindPropsMap[kind];

    return (
        <AlertBannerContainer kind={kind} className={className} style={style} testID={testID}>
            <AlertBannerContent>
                {renderIcon(kindProps.icon, {
                    size: 16,
                    className: clsx('flex shrink-0', kindProps.iconColor),
                })}
                {title ? <AlertTitle title={title} titleClass="font-[460]" /> : null}
                {details ? <AlertDetails details={details} /> : null}
                {action ? (
                    <AlertAction action={action} actionKind={kindProps.actionKind} size="small" />
                ) : null}
            </AlertBannerContent>
            {onClose ? (
                <AlertCloseButton onClose={onClose} centered={true} className="mr-2" />
            ) : null}
        </AlertBannerContainer>
    );
}

function AlertBannerContainer(
    props: AlertBannerProps & {
        className?: string;
        style?: React.CSSProperties;
        testID?: string;
        children: React.ReactNode;
    }
): React.ReactElement {
    const { kind = 'default', className, style, testID, children } = props;

    const kindProps = alertKindPropsMap[kind];

    return (
        <div
            className={clsx(
                '@container relative mt-1.5 flex w-full justify-between gap-2 border-y p-1',
                kindProps.border,
                kindProps.bg,
                className
            )}
            style={style}
            data-testid={testID}
        >
            {children}
        </div>
    );
}

function AlertBannerContent(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;

    return <div className="flex w-full items-center justify-center gap-2">{children}</div>;
}
